import type { LucideIcon } from 'lucide-react';
import { Lock } from 'lucide-react';

import { cn } from '@/shared/lib/cn';

import type { SectionStatus } from './clinical-status';

export interface StepperItem {
  key: string;
  label: string;
  icon: LucideIcon;
  status: SectionStatus;
}

interface ClinicalSectionStepperProps {
  items: StepperItem[];
  active: string;
  onSelect: (key: string) => void;
}

const DOT_CLASS: Record<SectionStatus, string> = {
  complete: 'bg-emerald-500',
  in_progress: 'bg-amber-500',
  pending: 'bg-slate-300',
  optional: 'bg-slate-300',
  locked: 'bg-slate-300',
};

/** Icon + label + status-dot tab row, replacing the flat Button-per-tab row. Horizontally
 *  scrollable so it degrades to mobile without a separate layout. */
export function ClinicalSectionStepper({
  items,
  active,
  onSelect,
}: ClinicalSectionStepperProps): JSX.Element {
  return (
    <div
      role="tablist"
      className="flex gap-1 overflow-x-auto border-b pb-0"
      aria-label="clinical-record-sections"
    >
      {items.map((item) => {
        const is_active = item.key === active;
        const is_locked = item.status === 'locked';
        const Icon = item.icon;
        return (
          <button
            key={item.key}
            type="button"
            role="tab"
            aria-selected={is_active}
            aria-disabled={is_locked}
            disabled={is_locked}
            onClick={() => {
              if (!is_locked) onSelect(item.key);
            }}
            className={cn(
              'flex shrink-0 flex-col items-center gap-1 border-b-2 px-3 pb-2 pt-1 text-xs transition-colors',
              is_active
                ? 'border-primary text-primary'
                : 'border-transparent text-muted-foreground hover:text-foreground',
              is_locked && 'cursor-not-allowed opacity-50',
            )}
          >
            <span className="relative">
              <span
                className={cn(
                  'flex h-8 w-8 items-center justify-center rounded-full',
                  is_active ? 'bg-primary text-primary-foreground' : 'bg-muted',
                )}
              >
                <Icon className="h-4 w-4" aria-hidden />
              </span>
              {is_locked ? (
                <Lock
                  className="bg-background absolute -right-1 -top-1 h-3.5 w-3.5 rounded-full p-0.5 text-slate-500"
                  aria-hidden
                />
              ) : null}
            </span>
            <span className={cn('whitespace-nowrap', is_active && 'font-semibold')}>
              {item.label}
            </span>
            {!is_locked ? (
              <span className={cn('h-1.5 w-1.5 rounded-full', DOT_CLASS[item.status])} aria-hidden />
            ) : null}
          </button>
        );
      })}
    </div>
  );
}
